import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, RefreshCw, Eye, Clock, UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  SUBMITTED: "bg-slate-100 text-slate-700",
  ACKNOWLEDGED: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  REOPENED: "bg-red-100 text-red-700",
};

export default function RequestServiceMyAssigned() {
  const auth = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchAssigned = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      const res = await axios.get(`${API}/request/requests?assigned_to=${auth.user?.user_id}&${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(res.data);
    } catch (err) {
      toast.error("Failed to load assigned requests");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAssigned();
  }, [statusFilter]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800">
          My Assigned
        </h1>
        <p className="text-slate-500 mt-1">Requests assigned to you</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="w-44">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="SUBMITTED">Submitted</SelectItem>
              <SelectItem value="ACKNOWLEDGED">Acknowledged</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="REOPENED">Reopened</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={fetchAssigned}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <p className="text-lg">No assigned requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <Link
              key={req.id}
              to={`/request-service/my-requests/${req.id}`}
              className="block bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        STATUS_STYLES[req.status] || "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {req.status?.replace("_", " ")}
                    </span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      {req.department}
                    </span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      {req.priority}
                    </span>
                  </div>
                  <h3 className="font-semibold text-slate-800">{req.service_type}</h3>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{req.description}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <UserCheck className="w-3 h-3" />
                      {req.requested_by_name || req.requested_by}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {req.created_at ? new Date(req.created_at).toLocaleDateString() : "-"}
                    </span>
                  </div>
                </div>
                <Eye className="w-5 h-5 text-slate-300 ml-4" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
